import * as React from "react"
import { graphql } from "gatsby"

import Layout from "../components/layout"
import Seo from "../components/seo"
import Breadcrumbs from "../components/breadcrumbs"
import PageTitle from "../components/page-title"
import Section from "../components/section"
import BlockTitle from "../components/block-title"
import Button from "../components/button"
import FormBuyersEnquiry from "../components/form-buyers-enquiry"
import ListingSlider from "../components/listing-slider"
import BlockContent from "../components/block-content"

export const query = graphql`
  query ($id: String!) {
    sanityListing(id: { eq: $id }) {
      title
      price
      status
      images {
        _key
        alt
        asset {
          gatsbyImageData(width: 1232)
        }
      }
      specifications {
        beam
        cabins
        berths
        draft
        airDraft
        waterCapacity
        driveType
        fuelType
        fuelCapacity
        heads
        loa
        location
        manufacturer
        maxSpeed
        model
        year
        numberOfEngines
        engineMake
        engineModel
        engineHorsepower
        style
      }
      virtualTour
      virtualTourEmbed
      brochure {
        asset {
          url
        }
      }
      _rawDescription
      _rawBoatSpecification
      _rawAccommodation
      _rawServiceHistory
      archived
    }
  }
`

const listingTemplate = ({ data }) => {
  const listing = data.sanityListing

  return (
    <div>
      <Layout>
        <Breadcrumbs
          title={listing.title}
          pageParentPath={"/listings"}
          pageParentTitle={"Listings"}
        />
        <PageTitle title={listing.title} />
        <Section className={"pt-0"}>
          <div className={"relative max-w-7xl mx-auto px-6 lg:px-12"}>
            <div
              className={
                "absolute top-0 right-6 lg:right-12 flex flex-col z-10"
              }
            >
              {/* Status */}
              {listing.status && listing.status !== "For Sale" && (
                <div className={"bg-blue py-1 text-center"}>
                  <span className={"text-sm uppercase font-bold text-navy"}>
                    {listing.status}
                  </span>
                </div>
              )}
              {listing.archived && (
                <div className={"bg-navy px-6 lg:px-8 py-3 lg:py-4"}>
                  <span
                    className={
                      "text-xl md:text-2xl lg:text-3xl text-blue font-light"
                    }
                  >
                    No Longer Available
                  </span>
                </div>
              )}
              {!listing.archived && (
                <div className={"bg-navy px-6 lg:px-8 py-3 lg:py-4"}>
                  <span
                    className={
                      "text-xl md:text-2xl lg:text-3xl text-blue font-light"
                    }
                  >
                    {listing.price}
                  </span>
                </div>
              )}
            </div>

            <ListingSlider images={listing.images} />
          </div>
          <div className={"max-w-7xl mx-auto px-6 lg:px-12"}>
            <div className={"grid lg:grid-cols-3 gap-0"}>
              <div className={"lg:col-span-2 bg-white p-8 flex flex-col gap-8"}>
                {/* Key Features */}
                <div>
                  <BlockTitle
                    level={"3"}
                    title={"Specifications"}
                    titleSize={"text-xl"}
                  />

                  <div className={"md:col-span-2 pt-6"}>
                    <div className={"grid grid-cols-2 md:grid-cols-3"}>
                      <dl className={"mb-4 md:mb-0"}>
                        {listing.specifications?.loa != null && (
                          <>
                            <dt>LOA:</dt>
                            <dd>{listing.specifications.loa}</dd>
                          </>
                        )}

                        {listing.specifications?.beam != null && (
                          <>
                            <dt>Beam:</dt>
                            <dd>{listing.specifications.beam}</dd>
                          </>
                        )}

                        {listing.specifications?.draft != null && (
                          <>
                            <dt>Draft:</dt>
                            <dd>{listing.specifications.draft}</dd>
                          </>
                        )}

                        {listing.specifications?.airDraft != null && (
                          <>
                            <dt>Air Draft:</dt>
                            <dd>{listing.specifications.airDraft}</dd>
                          </>
                        )}

                        {listing.specifications?.waterCapacity != null && (
                          <>
                            <dt>Water Capacity:</dt>
                            <dd>{listing.specifications.waterCapacity}</dd>
                          </>
                        )}

                        {listing.specifications?.fuelType != null && (
                          <>
                            <dt>Fuel Type:</dt>
                            <dd>{listing.specifications.fuelType}</dd>
                          </>
                        )}

                        {listing.specifications?.fuelCapacity != null && (
                          <>
                            <dt>Fuel Capacity:</dt>
                            <dd>{listing.specifications.fuelCapacity}</dd>
                          </>
                        )}

                        {listing.specifications?.engineHorsepower != null && (
                          <>
                            <dt>Engine Horsepower:</dt>
                            <dd>{listing.specifications.engineHorsepower}</dd>
                          </>
                        )}
                      </dl>

                      <dl>
                        {listing.specifications?.manufacturer != null && (
                          <>
                            <dt>Manufacturer:</dt>
                            <dd>{listing.specifications.manufacturer}</dd>
                          </>
                        )}

                        {listing.specifications?.model != null && (
                          <>
                            <dt>Model:</dt>
                            <dd>{listing.specifications.model}</dd>
                          </>
                        )}

                        {listing.specifications?.numberOfEngines != null && (
                          <>
                            <dt>Number of Engines:</dt>
                            <dd>{listing.specifications.numberOfEngines}</dd>
                          </>
                        )}

                        {listing.specifications?.engineMake != null && (
                          <>
                            <dt>Engine Make:</dt>
                            <dd>{listing.specifications.engineMake}</dd>
                          </>
                        )}

                        {listing.specifications?.engineModel != null && (
                          <>
                            <dt>Engine Model:</dt>
                            <dd>{listing.specifications.engineModel}</dd>
                          </>
                        )}

                        {listing.specifications?.year != null && (
                          <>
                            <dt>Year:</dt>
                            <dd>{listing.specifications.year}</dd>
                          </>
                        )}

                        {listing.specifications?.maxSpeed != null && (
                          <>
                            <dt>Max Speed:</dt>
                            <dd>{listing.specifications.maxSpeed}</dd>
                          </>
                        )}
                      </dl>

                      <dl>
                        {listing.specifications?.berths != null && (
                          <>
                            <dt>Berths:</dt>
                            <dd>{listing.specifications.berths}</dd>
                          </>
                        )}

                        {listing.specifications?.cabins != null && (
                          <>
                            <dt>Cabins:</dt>
                            <dd>{listing.specifications.cabins}</dd>
                          </>
                        )}

                        {listing.specifications?.heads != null && (
                          <>
                            <dt>Heads:</dt>
                            <dd>{listing.specifications.heads}</dd>
                          </>
                        )}

                        {listing.specifications?.driveType != null && (
                          <>
                            <dt>Drive Type:</dt>
                            <dd>{listing.specifications.driveType}</dd>
                          </>
                        )}

                        {listing.specifications?.location != null && (
                          <>
                            <dt>Location:</dt>
                            <dd>{listing.specifications.location}</dd>
                          </>
                        )}

                        {listing.specifications?.style != null && (
                          <>
                            <dt>Style:</dt>
                            <dd>{listing.specifications.style}</dd>
                          </>
                        )}
                      </dl>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className={"md:grid md:grid-cols-3 md:gap-12"}>
                  <div>
                    <BlockTitle
                      level={"3"}
                      title={"Description"}
                      titleSize={"text-xl"}
                    />
                  </div>

                  <div className={"md:col-span-2 md:pt-8"}>
                    <BlockContent blocks={listing._rawDescription} />
                  </div>
                </div>
              </div>

              <div className={"w-full p-6 flex flex-col gap-2"}>
                {listing.brochure && listing.brochure.asset.url && (
                  <Button
                    variant="dark"
                    fullWidth
                    href={listing.brochure.asset.url}
                    target={"_blank"}
                    rel={"noreferrer"}
                  >
                    Download Brochure
                  </Button>
                )}

                {listing.virtualTour && (
                  <Button
                    variant="dark"
                    fullWidth
                    href={listing.virtualTour}
                    target={"_blank"}
                    rel={"noreferrer"}
                  >
                    View Virtual Tour
                  </Button>
                )}

                <Button variant="primary" fullWidth href={"#enquire"}>
                  Enquire Now
                </Button>
              </div>
            </div>
          </div>
        </Section>

        {listing._rawBoatSpecification &&
          listing._rawBoatSpecification.length > 0 && (
            <Section className={"!pt-0"}>
              <div className={"max-w-7xl mx-auto px-6 lg:px-12"}>
                <div className={"bg-white p-8"}>
                  <BlockTitle
                    level={"3"}
                    title={"Boat Specification"}
                    titleSize={"text-2xl"}
                  />

                  <div>
                    <BlockContent
                      blocks={listing._rawBoatSpecification}
                      listColumns="2"
                    />
                  </div>
                </div>
              </div>
            </Section>
          )}

        {listing._rawAccommodation && listing._rawAccommodation.length > 0 && (
          <Section className={"!pt-0"}>
            <div className={"max-w-7xl mx-auto px-6 lg:px-12"}>
              <div className={"bg-white p-8"}>
                <BlockTitle
                  level={"3"}
                  title={"Accommodation"}
                  titleSize={"text-2xl"}
                />

                <div>
                  <BlockContent
                    blocks={listing._rawAccommodation}
                    listColumns="2"
                  />
                </div>
              </div>
            </div>
          </Section>
        )}

        {listing._rawServiceHistory &&
          listing._rawServiceHistory.length > 0 && (
            <Section className={"!pt-0"}>
              <div className={"max-w-7xl mx-auto px-6 lg:px-12"}>
                <div className={"bg-white p-8"}>
                  <BlockTitle
                    level={"3"}
                    title={"Service History"}
                    titleSize={"text-2xl"}
                  />

                  <div>
                    <BlockContent
                      blocks={listing._rawServiceHistory}
                      listColumns="2"
                    />
                  </div>
                </div>
              </div>
            </Section>
          )}

        {listing.virtualTourEmbed && listing.virtualTourEmbed.length > 0 && (
          <Section className={"!pt-0"}>
            <div className={"max-w-7xl mx-auto px-6 lg:px-12"}>
              <div className={"bg-white p-8"}>
                <BlockTitle
                  level={"3"}
                  title={"Virtual Tour"}
                  titleSize={"text-2xl"}
                />

                <div
                  className={"relative h-0 overflow-hidden max-w-full"}
                  style={{
                    paddingBottom: "56.25%",
                  }}
                >
                  <iframe
                    title={listing.title}
                    src={listing.virtualTourEmbed}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>

                {/* <iframe
                  width="100%"
                  height="594"
                  src={listing.virtualTourEmbed}
                  title={listing.title}
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerpolicy="strict-origin-when-cross-origin"
                  allowfullscreen
                ></iframe> */}
              </div>
            </div>
          </Section>
        )}

        <Section className={"!pt-0 !pb-0"} id={"enquire"}>
          <FormBuyersEnquiry
            title={`Interested in the ${listing.title}?`}
            listing={listing.title}
          />
        </Section>
      </Layout>
    </div>
  )
}

export const Head = ({ data }) => <Seo title={data.sanityListing.title} />

export default listingTemplate
